import React, { useState, useCallback, useEffect } from 'react';
import texasApi, {
    StrPortfolio,
    StrPortfolioProperty,
    StrPortfolioSummary,
} from '../../services/texasApi';

/**
 * STR Portfolio Dashboard (F-249, E-044, US-203 — T-203-002)
 * Columns: property, city, registration status, occupancy, tax owed
 * (T-203-002 AC); city filter narrows results (US-203 AC 4); summary
 * stats + CSV export ride the /export endpoint (T-203-003).
 */

const CITY_FILTERS: Array<{ value: string; label: string }> = [
    { value: '', label: 'All cities' },
    { value: 'houston', label: 'Houston' },
    { value: 'dallas', label: 'Dallas' },
];

const STATUS_BADGES: Record<string, string> = {
    ACTIVE: 'bg-emerald-100 text-emerald-700',
    PENDING: 'bg-sky-100 text-sky-700',
    EXPIRED: 'bg-red-100 text-red-700',
    UNREGISTERED: 'bg-gray-100 text-gray-600',
    PROHIBITED: 'bg-red-100 text-red-700',
    VERIFICATION_REQUIRED: 'bg-amber-100 text-amber-700',
};

function occupancyBadge(status?: string | null): string {
    if (status === 'BLOCK') return 'bg-red-100 text-red-700';
    if (status === 'WARN') return 'bg-amber-100 text-amber-700';
    if (status === 'OK') return 'bg-emerald-100 text-emerald-700';
    return 'bg-gray-100 text-gray-500';
}

function formatUsd(value?: number | null): string {
    if (value === null || value === undefined) return '—';
    return `$${value.toFixed(2)}`;
}

export const StrPortfolioDashboard: React.FC = () => {
    const [portfolio, setPortfolio] = useState<StrPortfolio | null>(null);
    const [cityFilter, setCityFilter] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadData = useCallback(async (city: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await texasApi.strPortfolio.getPortfolio(city || undefined);
            setPortfolio(data);
        } catch (err: any) {
            setError(err?.message || 'Failed to load STR portfolio');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(cityFilter); }, [loadData, cityFilter]);

    const properties: StrPortfolioProperty[] = portfolio?.properties ?? [];
    const summary: StrPortfolioSummary | null = portfolio?.summary ?? null;

    const handleExport = async () => {
        try {
            const blob = await texasApi.strPortfolio.exportCsv(cityFilter || undefined);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'str-portfolio.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setError('Failed to export STR portfolio CSV');
        }
    };

    return (
        <div data-testid="str-portfolio-dashboard" className="texas-page-container">
            <h1>STR Portfolio Dashboard</h1>
            <p className="page-description">
                Short-term rental registrations, occupancy, and tax liability across cities.
            </p>

            {error && <div className="error" data-testid="error">{error}</div>}

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-xs font-bold text-slate-500" htmlFor="str-city-filter">
                    City
                </label>
                <select
                    id="str-city-filter"
                    data-testid="str-city-filter"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                    {CITY_FILTERS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                </select>
                <button
                    type="button"
                    data-testid="str-export-btn"
                    onClick={handleExport}
                    className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 transition shadow-sm"
                >
                    <i className="fas fa-file-csv text-xs"></i> Export CSV
                </button>
            </div>

            {summary && (
                <section className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4" data-testid="str-summary">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <div className="text-xs font-bold text-slate-500 tracking-wider">PROPERTIES</div>
                        <div className="text-3xl font-black text-slate-900 mt-1">{summary.totalProperties}</div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <div className="text-xs font-bold text-emerald-600 tracking-wider">ACTIVE</div>
                        <div className="text-3xl font-black text-emerald-600 mt-1">{summary.activeRegistrations}</div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <div className="text-xs font-bold text-amber-600 tracking-wider">WARNINGS</div>
                        <div className="text-3xl font-black text-amber-600 mt-1">{summary.occupancyWarnings}</div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <div className="text-xs font-bold text-red-600 tracking-wider">TAX OWED</div>
                        <div className="text-3xl font-black text-red-600 mt-1">{formatUsd(summary.totalAnnualTaxOwedUsd)}</div>
                    </div>
                </section>
            )}

            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                {loading ? (
                    <div className="no-results">Loading STR portfolio...</div>
                ) : properties.length === 0 ? (
                    <div className="no-results" data-testid="str-empty">
                        No STR properties found{cityFilter ? ` in ${cityFilter}` : ''}.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table data-testid="str-portfolio-table" className="w-full text-left text-sm">
                            <thead>
                                <tr className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-gray-200">
                                    <th className="py-2 pr-4">Property</th>
                                    <th className="py-2 pr-4">City</th>
                                    <th className="py-2 pr-4">Registration</th>
                                    <th className="py-2 pr-4">Occupancy</th>
                                    <th className="py-2 pr-4">Tax owed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {properties.map((p) => (
                                    <tr key={`${p.city}-${p.propertyId}`} data-testid="str-row" className="border-b border-gray-100">
                                        <td className="py-2 pr-4 font-bold text-gray-900">{p.propertyId}</td>
                                        <td className="py-2 pr-4">{p.cityLabel || p.city}</td>
                                        <td className="py-2 pr-4">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_BADGES[p.registrationStatus] || 'bg-gray-100 text-gray-600'}`}>
                                                {p.registrationStatus}
                                            </span>
                                            {p.daysUntilExpiry != null && (
                                                <span className="ml-2 text-[10px] text-slate-500">{p.daysUntilExpiry}d</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4">
                                            {p.occupancy ? (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${occupancyBadge(p.occupancy.status)}`}>
                                                    {p.occupancy.currentGuests}/{p.occupancy.maxOccupancy} ({p.occupancy.utilizationPct}%)
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4">{formatUsd(p.taxOwedUsd)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default StrPortfolioDashboard;
